import { RULE_SET_ID_PATTERN, type PatternBased, type RuleAction, type RuleBased, type RuleSet, type RuleSetStatus, type SetRule } from '@kkp-fraud/shared';

/**
 * 2c rule sets — the pure half: the admin service's `/api/rulesets` wire shapes and what the
 * console checks before it sends (the service and the engine decide; these mirror their simple
 * rules so an operator sees a problem before a round trip, not instead of one).
 *
 * Every enabled set evaluates every transaction and the strongest decision wins. The PRIMARY
 * set supplies the fail-safe policy and is what `/api/rules…` (and so every screen but Rule
 * Management) works on. No hard delete: archiving takes a set out of evaluation; its releases
 * stay as the audit record.
 */

export { RULE_SET_ID_PATTERN };

export const RULE_SET_ROUTES = {
  ruleSets: '/api/rulesets',
} as const;

export const ruleSetPath = (id: string, suffix = ''): string => `${RULE_SET_ROUTES.ruleSets}/${encodeURIComponent(id)}${suffix}`;

/** The pattern event that counts a set's past fires (key `v:{scope}:{value}:set_fired:{setId}`). */
export const SET_FIRED = 'set_fired' as const;

/** `GET /api/rulesets` row. */
export interface RuleSetView {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  primary: boolean;
  archived: boolean;
  createdUtc?: string;
  createdBy?: string;
  updatedUtc?: string;
  updatedBy?: string;
  /** The active release; null = none yet (publishing one fixes it). */
  version: string | null;
  ruleCount: number;
  enabledRuleCount: number;
  setRuleCount: number;
  lastRelease?: { version: string; savedUtc: string; actor: string; comment?: string };
  /**
   * R8b: what the signed-in user may do with this set — the admin service lists only the sets
   * the user may see. Absent from an older service, password mode and the fallback list: then
   * the global role decides, as before R8b.
   */
  access?: RuleSetAccess;
  /** Wave 5: a duplicate names its source release; its page seeds the draft from it until the first publish. */
  copiedFrom?: CopiedFrom;
}

/** `GET /api/rulesets` (and every registry write's answer). */
export interface RuleSetList {
  /** `cosmos` (full CRUD) or `proxy` (the decision API's one set, read only). */
  source: 'cosmos' | 'proxy';
  /** False: this service cannot write the registry (proxy mode, password mode, demo). */
  canManage: boolean;
  /** The registry's etag — sent back as `If-Match` on every write. */
  etag: string;
  sets: RuleSetView[];
}

export interface RuleSetCreateRequest {
  id: string;
  name: string;
  description?: string;
  enabled?: boolean;
}

export interface RuleSetUpdateRequest {
  name?: string;
  /** `''` removes it. */
  description?: string;
  enabled?: boolean;
}

/** The primary first, then the rest in registry order. */
export function orderedSets(sets: readonly RuleSetView[]): RuleSetView[] {
  return [...sets].sort((a, b) => Number(b.primary) - Number(a.primary));
}

/** Sets a picker offers: not archived. */
export function liveSets(sets: readonly RuleSetView[]): RuleSetView[] {
  return orderedSets(sets).filter((s) => !s.archived);
}

export function primaryOf(sets: readonly RuleSetView[]): RuleSetView | undefined {
  return sets.find((s) => s.primary);
}

/** "Card not present (cnp-risk)" — a name alone can repeat, the id cannot. */
export function setLabel(s: Pick<RuleSetView, 'id' | 'name'>): string {
  return s.name && s.name !== s.id ? `${s.name} (${s.id})` : s.id;
}

/** The engine's `/readyz` line for a set, when it reports one. */
export function engineStatusOf(id: string, statuses: readonly RuleSetStatus[] | undefined): RuleSetStatus | undefined {
  return statuses?.find((x) => x.id === id);
}

export type RuleSetFormProblem = 'id' | 'id_taken' | 'name' | 'name_long' | 'description_long';

/** What the create / edit dialog refuses before sending (`RuleSetEndpoints`' own checks). */
export function ruleSetFormProblems(
  form: { id: string; name: string; description: string },
  existingIds: readonly string[],
  creating: boolean,
): RuleSetFormProblem[] {
  const out: RuleSetFormProblem[] = [];
  if (creating) {
    if (!RULE_SET_ID_PATTERN.test(form.id)) out.push('id');
    else if (existingIds.includes(form.id)) out.push('id_taken');
  }
  if (form.name.trim().length === 0) out.push('name');
  else if (form.name.trim().length > 100) out.push('name_long');
  if (form.description.trim().length > 500) out.push('description_long');
  return out;
}

// ---- set-level rules (a) ------------------------------------------------------

/** The actions a hit may carry; absent `countActions` = every one of them but APPROVE. */
export const COUNTABLE_ACTIONS: readonly RuleAction[] = ['DECLINE', 'STEP_UP', 'APPROVE_NOTIFY', 'ALERT_ONLY', 'APPROVE'];
export const DEFAULT_COUNT_ACTIONS: readonly RuleAction[] = COUNTABLE_ACTIONS.filter((a) => a !== 'APPROVE');

/** Every rule of the set by id — what `of` may name. */
export function setRuleCandidates(rs: RuleSet): (RuleBased | PatternBased)[] {
  return [...(rs.ruleBased ?? []), ...(rs.patternBased ?? [])];
}

/** How many rules a set rule counts: its `of`, else every rule of the set. */
export function countedRules(rule: SetRule, rs: RuleSet): number {
  return rule.of ? rule.of.length : setRuleCandidates(rs).length;
}

/** SR-01, SR-02 … → the first free id in the same style. */
export function nextSetRuleId(existing: readonly string[]): string {
  const used = new Set(existing);
  for (let n = 1; n < 1000; n++) {
    const id = `SR-${String(n).padStart(2, '0')}`;
    if (!used.has(id)) return id;
  }
  return `SR-${Date.now()}`;
}

/**
 * A new set rule: "2 or more rules of this set → STEP_UP", switched off until reviewed.
 * `points` is unused (no scoring) but still required by the document's shape check.
 */
export function newSetRule(existing: readonly string[]): SetRule {
  return { id: nextSetRuleId(existing), name: '', enabled: false, minHits: 2, action: 'STEP_UP', points: 50 };
}

/**
 * A set rule as stored: `of` / `countActions` absent (never `undefined`, never an empty array
 * meaning "all") when they mean the default.
 */
export function cleanSetRule(rule: SetRule): SetRule {
  const out: SetRule = { ...rule };
  if (out.of === undefined) delete out.of;
  if (out.countActions === undefined) delete out.countActions;
  return out;
}

/** `rs` with `setRules` replaced — the key removed when there are none, so an untouched document stays byte-identical. */
export function withSetRules(rs: RuleSet, rules: readonly SetRule[]): RuleSet {
  const next: RuleSet = { ...rs };
  if (rules.length > 0) next.setRules = rules.map(cleanSetRule);
  else delete next.setRules;
  return next;
}

// ---- R8b per-rule-set permissions ------------------------------------------------

/** One user's level on one set. `publish` implies `edit` implies `view`. */
export type RuleSetAccess = 'view' | 'edit' | 'publish';

/** Every level, weakest first — the columns of the Users dialog's matrix. */
export const RULE_SET_ACCESS_LEVELS: readonly RuleSetAccess[] = ['view', 'edit', 'publish'];

const RANK: Record<RuleSetAccess | 'none', number> = { none: 0, view: 1, edit: 2, publish: 3 };

/** Does `has` include `needs`? `undefined` = not known (see `RuleSetView.access`). */
export function accessAtLeast(has: RuleSetAccess | 'none' | undefined, needs: RuleSetAccess): boolean | undefined {
  return has === undefined ? undefined : RANK[has] >= RANK[needs];
}

/**
 * The caller's level on the PRIMARY set, from the registry the admin service answered: a list
 * without a primary means the primary is hidden from this user (`'none'`); no list, or a primary
 * without `access` (an older service), is not known (`undefined`).
 */
export function primaryAccess(list: RuleSetList | null | undefined): RuleSetAccess | 'none' | undefined {
  // Anything but a real listing (an older service, a test double answering another route) is not known.
  if (!list || !Array.isArray(list.sets)) return undefined;
  const primary = list.sets.find((s) => s.primary);
  if (!primary) return list.sets.some((s) => s.access !== undefined) ? 'none' : undefined;
  return primary.access;
}

/** The caller's level on set `id` — as `primaryAccess`: absent from a list that carries levels = `'none'`. */
export function setAccessIn(list: RuleSetList | null | undefined, id: string | null | undefined): RuleSetAccess | 'none' | undefined {
  if (!list || !Array.isArray(list.sets) || !id) return undefined;
  const set = list.sets.find((s) => s.id === id);
  if (!set) return list.sets.some((s) => s.access !== undefined) ? 'none' : undefined;
  return set.access;
}

/** A user's stored per-set map (`null` = none yet: the role decides on every set). */
export type RuleSetPermissionMap = Record<string, RuleSetAccess>;

/** The most a global role allows on any set (mirrors `RuleSetPermissions.Ceiling`). Admin: everything. */
export function roleCeiling(roles: readonly string[]): RuleSetAccess | 'none' {
  const has = (r: string) => roles.some((x) => x.toLowerCase() === r.toLowerCase());
  if (has('Admin') || has('Editor') || has('Approver')) return 'publish';
  if (has('Viewer')) return 'view';
  return 'none';
}

/** `effective(user, set)` — the admin service's rule, for the Users dialog's live preview. */
export function effectiveAccess(roles: readonly string[], map: RuleSetPermissionMap | null | undefined, setId: string): RuleSetAccess | 'none' {
  const ceiling = roleCeiling(roles);
  if (roles.some((r) => r.toLowerCase() === 'admin')) return 'publish';
  if (map === null || map === undefined) return ceiling;
  const granted = map[setId] ?? 'none';
  return RANK[granted] <= RANK[ceiling] ? granted : ceiling;
}

/** A map with a level for each of `setIds` — the role's own level — to start an explicit map from. */
export function mapFromRole(roles: readonly string[], setIds: readonly string[]): RuleSetPermissionMap {
  const out: RuleSetPermissionMap = {};
  const level = roleCeiling(roles);
  if (level === 'none') return out;
  for (const id of setIds) out[id] = level;
  return out;
}

// ---- Wave 5: duplicate a set as a draft ----------------------------------------

/**
 * The version a set's first release carries — the empty document `POST /api/rulesets` (and a
 * duplicate) publishes at once (`CosmosRuleSets.FirstVersion`). A publish moves past it, and a
 * rollback releases a NEW version, so a live `1.0.0` is always the creation release.
 */
export const CREATION_VERSION = '1.0.0';

/** On a duplicate's registry entry: the set and the release its content is copied from. */
export interface CopiedFrom {
  setId: string;
  version: string;
}

/** `POST /api/rulesets/{source}/duplicate` — the new set's id, name and description. */
export interface RuleSetDuplicateRequest {
  id: string;
  name: string;
  description?: string;
}

/** The primary's id from a registry answer — undefined for no answer, or anything but a real listing. */
export function primaryIdIn(list: RuleSetList | null | undefined): string | undefined {
  return list && Array.isArray(list.sets) ? primaryOf(list.sets)?.id : undefined;
}

/** A set's row in a registry answer (the same caution as `primaryIdIn`). */
export function setViewIn(list: RuleSetList | null | undefined, id: string): RuleSetView | undefined {
  return list && Array.isArray(list.sets) ? list.sets.find((s) => s.id === id) : undefined;
}

/**
 * The copy a duplicate still waits to publish: its entry names a source (`copiedFrom`) and its
 * live document is still the creation release — `1.0.0` with no rules of any kind. Null once
 * the first publish moved it on (or for any set that is not a duplicate).
 */
export function awaitsCopySeed(view: Pick<RuleSetView, 'copiedFrom'> | undefined, live: RuleSet | null): CopiedFrom | null {
  const from = view?.copiedFrom;
  if (!from || !from.setId || !from.version || !live) return null;
  const rules = (live.ruleBased ?? []).length + (live.patternBased ?? []).length + (live.setRules ?? []).length;
  return live.version === CREATION_VERSION && rules === 0 ? from : null;
}

/**
 * The source release as the new set's draft: its content, under the new set's `ruleSetId` (the
 * set's publish route refuses any other) and the live document's `id` / `version` (what the
 * release compares against). Everything else — rules, set rules, thresholds, fail-safe — is the
 * source's, so the diff against the empty live shows the whole copy.
 *
 * A `set_fired` measure that counts the SOURCE set's own fires counts the copy's instead (the
 * set's id, where the document carries it); one that counts another set keeps it.
 */
export function seededCopy(source: RuleSet, live: RuleSet): RuleSet {
  const copy = JSON.parse(JSON.stringify(source)) as RuleSet;
  copy.id = live.id;
  copy.ruleSetId = live.ruleSetId;
  copy.version = live.version;
  if (live.updatedUtc !== undefined) copy.updatedUtc = live.updatedUtc;
  else delete copy.updatedUtc;
  const own = (m: { ruleSetId?: string }) => {
    if (m.ruleSetId !== undefined && m.ruleSetId === source.ruleSetId) m.ruleSetId = live.ruleSetId;
  };
  for (const rule of copy.patternBased ?? []) {
    own(rule);
    for (const branch of rule.branches ?? []) for (const measure of branch.measures ?? []) own(measure);
  }
  return copy;
}
